import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  ForgetPasswordToken,
  GenerateForgetPasswordOtpArgs,
  ValidateForgetPasswordOtpArgs,
} from 'src/auth/forget-password/forget-password';
import { apiResources } from 'src/common/Constants';
import { environment } from 'src/environments/environment';
import { transformResponses } from '../rtkq.interceptor';

/** RTK Query API slice for OTP-related authentication endpoints. */
export const otpsApi = createApi({
  reducerPath: 'otpsApi',
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    /** Mutation to generate OTP for a partner using their phone number. */
    generatePartnerOtp: builder.mutation<void, string>({
      query: (phoneNumber) => ({
        url: `${apiResources.generatePartnerOtp}/${phoneNumber}`,
        method: 'POST',
        body: { clientSecret: environment.VITE_CLIENT_SECRET_CSR },
      }),
    }),
    /** Query to validate a partner's OTP using phone number and OTP. */
    validatePartnerOtp: builder.query<void, { phoneNumber: string; otp: string }>({
      query: ({ phoneNumber, otp }) => `${apiResources.validatePartnerOtp}/${phoneNumber}/${otp}`,
    }),
    /** Mutation to generate OTP for a driver using their phone number. */
    generateDriverOtp: builder.mutation<void, string>({
      query: (phoneNumber) => ({
        url: `${apiResources.generateDriverOtp}/${phoneNumber}`,
        method: 'POST',
        body: { clientSecret: environment.VITE_CLIENT_SECRET_CSR },
      }),
    }),
    /** Query to validate a driver's OTP using phone number and OTP. */
    validateDriverOtp: builder.query<void, { phoneNumber: string; otp: string }>({
      query: ({ phoneNumber, otp }) => `${apiResources.validateDriverOtp}/${phoneNumber}/${otp}`,
    }),
    /** Query to generate a forget password OTP using username and email flag. */
    generateForgetPasswordOtp: builder.query<void, GenerateForgetPasswordOtpArgs>({
      query: ({ username, isEmail }) =>
        `${apiResources.generateForgetPasswordOtp}?username=${username}&isEmail=${isEmail}`,
    }),
    /** Mutation to validate a forget password OTP using username, OTP, and email flag. */
    validateForgetPasswordOtp: builder.mutation<ForgetPasswordToken, ValidateForgetPasswordOtpArgs>({
      query: ({ username, otp, isEmail }) => ({
        url: `${apiResources.validateForgetPasswordOtp}?username=${username}&otp=${otp}&isEmail=${isEmail}`,
        method: 'POST',
      }),
      ...transformResponses,
    }),
  }),
});

/** RTK Query hooks for OTP-related authentication endpoints. */
export const {
  useGeneratePartnerOtpMutation,
  useLazyValidatePartnerOtpQuery,
  useGenerateDriverOtpMutation,
  useLazyValidateDriverOtpQuery,
  useLazyGenerateForgetPasswordOtpQuery,
  useValidateForgetPasswordOtpMutation,
} = otpsApi;
